// src/components/widgets/CustomQuestionsTab.jsx
import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import QuestionFormDialog from './QuestionFormDialog';
import { Separator } from '../ui/separator';

const CustomQuestionsTab = ({ questions = [], onChange }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setIsDialogOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsDialogOpen(true);
  };

  const handleSaveQuestion = (questionData) => {
    if (editingQuestion) {
      // Update existing question
      const updatedQuestions = questions.map(q =>
        q.id === questionData.id ? questionData : q
      );
      onChange(updatedQuestions);
    } else {
      // Add new question
      onChange([...questions, questionData]);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    if (confirm('Are you sure you want to delete this question?')) {
      onChange(questions.filter(q => q.id !== questionId));
    }
  };

  const handleMoveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= questions.length) return;

    // Swap questions
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];

    // Update display_order
    newQuestions.forEach((q, i) => {
      q.display_order = i;
    });

    onChange(newQuestions);
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      textarea: 'Long Text',
      select: 'Dropdown',
      radio: 'Radio',
      checkbox: 'Checkbox'
    };
    return labels[type] || type;
  };

  const getStepLabel = (step) => {
    return step === 'service' ? 'Service Step' : 'Contact Step';
  };

  // Sort questions by display_order
  const sortedQuestions = [...questions].sort((a, b) =>
    (a.display_order || 0) - (b.display_order || 0)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Custom Questions</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Add custom questions to collect additional information from customers.
          </p>
        </div>
        <Button onClick={handleAddQuestion} size="sm" className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Question
        </Button>
      </div>

      <Separator />

      {sortedQuestions.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground mb-3">
            No custom questions yet
          </p>
          <Button onClick={handleAddQuestion} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Question
          </Button>
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {sortedQuestions.map((question, index) => (
            <AccordionItem key={question.id} value={question.id}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2 flex-1 text-left">
                  <span className="font-medium text-sm">{question.label}</span>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {getQuestionTypeLabel(question.type)}
                    </Badge>
                    <Badge variant={question.show_on_step === 'service' ? 'default' : 'secondary'} className="text-xs">
                      {getStepLabel(question.show_on_step)}
                    </Badge>
                    {question.required && (
                      <Badge variant="destructive" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {/* Question Details */}
                  <div className="text-sm space-y-2">
                    {question.placeholder && (
                      <div>
                        <span className="text-muted-foreground">Placeholder:</span>{' '}
                        <span>{question.placeholder}</span>
                      </div>
                    )}
                    {question.help_text && (
                      <div>
                        <span className="text-muted-foreground">Help Text:</span>{' '}
                        <span>{question.help_text}</span>
                      </div>
                    )}
                    {question.options && question.options.length > 0 && (
                      <div>
                        <span className="text-muted-foreground">Options:</span>{' '}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {question.options.map((option, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {option}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {(question.validation?.min_length || question.validation?.max_length) && (
                      <div>
                        <span className="text-muted-foreground">Validation:</span>{' '}
                        {question.validation.min_length && (
                          <span>Min: {question.validation.min_length} </span>
                        )}
                        {question.validation.max_length && (
                          <span>Max: {question.validation.max_length}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Actions */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMoveQuestion(index, 'up')}
                        disabled={index === 0}
                        className="h-8"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMoveQuestion(index, 'down')}
                        disabled={index === sortedQuestions.length - 1}
                        className="h-8"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditQuestion(question)}
                        className="h-8"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="h-8"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Dialog for adding/editing questions */}
      <QuestionFormDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingQuestion(null);
        }}
        onSave={handleSaveQuestion}
        initialData={editingQuestion}
      />
    </div>
  );
};

export default CustomQuestionsTab;
